import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { useImpersonation } from '@/contexts/ImpersonationContext'
import type {
  Vehicle,
  VehicleFeature,
  VehiclePricing,
  VehiclePricingAddon,
  VehiclePricingTier,
} from '@limo/database'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { VehicleFeatureDialog } from '@/components/vehicles/VehicleFeatureDialog'
import { VehiclePricingSection } from '@/components/vehicles/VehiclePricingSection'
import { useToast } from '@/hooks/use-toast'
import {
  ArrowLeft,
  Pencil,
  Trash2,
  Plus,
  MoreHorizontal,
  Users,
  Briefcase,
  Car,
  Loader2,
  Sparkles,
} from 'lucide-react'

import { VEHICLE_TYPE_LABELS } from '@/schemas/vehicle'

type VehicleWithRelations = Vehicle & {
  vehicle_features: VehicleFeature[]
  vehicle_pricing: (VehiclePricing & {
    vehicle_pricing_addons: VehiclePricingAddon[]
    vehicle_pricing_tiers: VehiclePricingTier[]
  })[]
}

export function VehicleDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { account, isAdmin } = useAuth()
  const { isImpersonating, impersonatedAccount } = useImpersonation()

  const activeAccount = isImpersonating ? impersonatedAccount : account

  const basePath = isImpersonating
    ? `/admin/accounts/${impersonatedAccount?.slug}/view`
    : '/dashboard'

  // Feature dialog state
  const [featureDialogOpen, setFeatureDialogOpen] = useState(false)
  const [editFeatureId, setEditFeatureId] = useState<string | null>(null)
  const [deleteFeatureId, setDeleteFeatureId] = useState<string | null>(null)

  const { data: vehicle, isLoading, refetch } = useQuery({
    queryKey: ['vehicle-detail', id],
    queryFn: async () => {
      if (!id) return null
      const { data, error } = await supabase
        .from('vehicles')
        .select(
          `
          *,
          vehicle_features (*),
          vehicle_pricing (
            *,
            vehicle_pricing_addons (*),
            vehicle_pricing_tiers (*)
          )
        `
        )
        .eq('id', id)
        .single()
      if (error) throw error
      return data as VehicleWithRelations
    },
    enabled: !!id,
  })

  const deleteFeatureMutation = useMutation({
    mutationFn: async (featureId: string) => {
      const { error } = await supabase
        .from('vehicle_features')
        .delete()
        .eq('id', featureId)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vehicle-detail', id] })
      toast({ title: 'Feature deleted' })
      setDeleteFeatureId(null)
    },
    onError: (error) => {
      toast({ title: 'Error', description: error.message, variant: 'destructive' })
    },
  })

  if (!activeAccount || !id) {
    return null
  }

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!vehicle) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <p className="text-muted-foreground">Vehicle not found</p>
        <Button
          variant="outline"
          className="mt-4"
          onClick={() => navigate(`${basePath}/vehicles`)}
        >
          Back to Vehicles
        </Button>
      </div>
    )
  }

  const vehicleTypeLabel =
    VEHICLE_TYPE_LABELS[vehicle.vehicle_type as keyof typeof VEHICLE_TYPE_LABELS] ||
    vehicle.vehicle_type

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate(`${basePath}/vehicles`)}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold tracking-tight">{vehicle.name}</h1>
              {!vehicle.is_active && <Badge variant="secondary">Inactive</Badge>}
            </div>
            <div className="mt-1 flex items-center gap-3 text-sm text-muted-foreground">
              <Badge variant="outline">{vehicleTypeLabel}</Badge>
              {(vehicle.year || vehicle.make || vehicle.model) && (
                <span>
                  {[vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')}
                </span>
              )}
            </div>
          </div>
        </div>
        {isAdmin && (
          <Button onClick={() => navigate(`${basePath}/vehicles/${id}/edit`)}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Vehicle
          </Button>
        )}
      </div>

      {/* Vehicle Info */}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-1">
          <div className="aspect-video overflow-hidden rounded-lg bg-muted">
            {vehicle.image_url ? (
              <img
                src={vehicle.image_url}
                alt={vehicle.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <Car className="h-16 w-16 text-muted-foreground/50" />
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3">
                  <Users className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Passengers</p>
                    <p className="font-medium">{vehicle.passenger_capacity}</p>
                  </div>
                </div>
                {vehicle.luggage_capacity !== null && (
                  <div className="flex items-center gap-3">
                    <Briefcase className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Luggage</p>
                      <p className="font-medium">{vehicle.luggage_capacity} bags</p>
                    </div>
                  </div>
                )}
                {vehicle.color && (
                  <div>
                    <p className="text-sm text-muted-foreground">Color</p>
                    <p className="font-medium capitalize">{vehicle.color}</p>
                  </div>
                )}
              </div>
              {vehicle.description && (
                <p className="mt-4 text-muted-foreground">{vehicle.description}</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Features Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" />
            <CardTitle>Features</CardTitle>
            <Badge variant="secondary">{vehicle.vehicle_features?.length || 0}</Badge>
          </div>
          <Button size="sm" onClick={() => setFeatureDialogOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Feature
          </Button>
        </CardHeader>
        <CardContent>
          {vehicle.vehicle_features && vehicle.vehicle_features.length > 0 ? (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {vehicle.vehicle_features.map((feature) => (
                <div
                  key={feature.id}
                  className="flex items-center justify-between rounded-md border p-3"
                >
                  <span>{feature.name}</span>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setEditFeatureId(feature.id)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-destructive"
                        onClick={() => setDeleteFeatureId(feature.id)}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No features added yet</p>
          )}
        </CardContent>
      </Card>

      {/* Pricing Section - New inline cards with toggles */}
      <VehiclePricingSection
        vehicleId={id}
        accountId={activeAccount.id}
        pricing={vehicle.vehicle_pricing || []}
        onRefresh={() => refetch()}
      />

      {/* Feature Dialog */}
      <VehicleFeatureDialog
        featureId={editFeatureId}
        vehicleId={id}
        accountId={activeAccount.id}
        open={featureDialogOpen || !!editFeatureId}
        onOpenChange={(open) => {
          setFeatureDialogOpen(open)
          if (!open) setEditFeatureId(null)
          queryClient.invalidateQueries({ queryKey: ['vehicle-detail', id] })
        }}
      />

      {/* Delete Feature Confirmation */}
      <AlertDialog open={!!deleteFeatureId} onOpenChange={() => setDeleteFeatureId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Feature</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this feature? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteFeatureId && deleteFeatureMutation.mutate(deleteFeatureId)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
